import { useMemo, useState } from 'react'
import { FocusTrapDialog } from '../../components/FocusTrapDialog'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { ApiError } from '../../api/problem'
import { commitSpendImportBatch, previewSpendImportBatch, uploadSpendImportBatch } from '../../api/spendImport'
import { SPEND_IMPORT_CANONICAL_FIELDS } from '../../types/spendImport'
import type {
  SpendImportBatchView,
  SpendImportCommitResultView,
  SpendImportPreviewView,
} from '../../types/spendImport'

export interface SpendImportWizardProps {
  productionId: string
  /** Every fiscal year already recorded for this Production (`ProductionFYView.fiscalYearId`) — every row in one import batch targets the SAME fiscal year (a deliberate, disclosed simplification, docs/data-dictionary.md SpendImportBatch). */
  fiscalYearIds: string[]
  onClose: () => void
  /** Called once, after a successful commit — the caller re-fetches the production's spend lines. */
  onCommitted: () => void
}

type WizardStep = 'upload' | 'map' | 'preview' | 'summary'

const FIELD_LABEL: Record<string, string> = {
  category: 'Category (required)',
  amount: 'Amount (required)',
  currency: 'Currency (required)',
  phaseRef: 'Phase ref',
  vendorId: 'Vendor ID',
  personnelId: 'Personnel ID',
  evidenceRef: 'Evidence ref',
  qualifyingPctOverride: 'Qualifying % override',
  comment: 'Comment (required with an override)',
}

const ROW_STATUS_VARIANT: Record<string, StatusPillVariant> = {
  valid: 'success',
  warning: 'warning',
  error: 'danger',
  created: 'success',
  skipped: 'warning',
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

/**
 * The spend-line import wizard (IB-184, FR-M20.5/FR-M23.6): upload -> column-map -> preview
 * (ZERO writes) -> commit -> summary. Launched from `ProductionDetailScreen`'s "Qualified spend"
 * section. Every network call goes through `api/spendImport.ts` — never a raw `qualifyingPct`/
 * `inState`/`locked` object literal anywhere in this file (`noRawSpendWrite.test.ts` scans this
 * directory); this wizard's own override field is named `qualifyingPctOverride`.
 */
export function SpendImportWizard({ productionId, fiscalYearIds, onClose, onCommitted }: SpendImportWizardProps) {
  const [step, setStep] = useState<WizardStep>('upload')
  const [fiscalYearId, setFiscalYearId] = useState(fiscalYearIds[0] ?? '')
  const [file, setFile] = useState<File | null>(null)
  const [batch, setBatch] = useState<SpendImportBatchView | null>(null)
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({})
  const [preview, setPreview] = useState<SpendImportPreviewView | null>(null)
  const [commitResult, setCommitResult] = useState<SpendImportCommitResultView | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const noFiscalYear = fiscalYearIds.length === 0

  async function handleUpload() {
    if (!file || !fiscalYearId) return
    setBusy(true)
    setError(null)
    try {
      const uploaded = await uploadSpendImportBatch(productionId, fiscalYearId, file)
      setBatch(uploaded)
      setColumnMapping(uploaded.suggestedMapping)
      setStep('map')
    } catch (err) {
      setError(errorMessage(err, 'Could not upload this file. Please try again.'))
    } finally {
      setBusy(false)
    }
  }

  async function handlePreview() {
    if (!batch) return
    setBusy(true)
    setError(null)
    try {
      const result = await previewSpendImportBatch(batch.id, columnMapping)
      setPreview(result)
      setStep('preview')
    } catch (err) {
      setError(errorMessage(err, 'Could not validate this batch. Please try again.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleCommit() {
    if (!batch) return
    setBusy(true)
    setError(null)
    try {
      const result = await commitSpendImportBatch(batch.id, columnMapping)
      setCommitResult(result)
      setStep('summary')
    } catch (err) {
      setError(errorMessage(err, 'Could not commit this batch. Please try again.'))
    } finally {
      setBusy(false)
    }
  }

  const canCommit = useMemo(() => !!preview && preview.validCount > 0, [preview])

  return (
    <FocusTrapDialog titleId="spend-import-wizard-title" title="Import spend lines" onClose={onClose} width={720}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
        <WizardSteps step={step} />

        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}

        {step === 'upload' ? (
          <UploadStep
            noFiscalYear={noFiscalYear}
            fiscalYearIds={fiscalYearIds}
            fiscalYearId={fiscalYearId}
            onFiscalYearChange={setFiscalYearId}
            file={file}
            onFileChange={setFile}
          />
        ) : null}

        {step === 'map' && batch ? (
          <MapStep batch={batch} columnMapping={columnMapping} onChange={setColumnMapping} />
        ) : null}

        {step === 'preview' && preview ? <PreviewStep preview={preview} /> : null}

        {step === 'summary' && commitResult ? <SummaryStep result={commitResult} /> : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--iw-space-2)' }}>
          {step === 'upload' ? (
            <>
              <button type="button" className="iw-btn" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="iw-btn iw-btn--primary"
                disabled={!file || !fiscalYearId || busy || noFiscalYear}
                onClick={() => void handleUpload()}
              >
                <Icon name="upload" size={14} /> Upload
              </button>
            </>
          ) : null}

          {step === 'map' ? (
            <>
              <button type="button" className="iw-btn" onClick={() => setStep('upload')}>
                Back
              </button>
              <button type="button" className="iw-btn iw-btn--primary" disabled={busy} onClick={() => void handlePreview()}>
                Preview
              </button>
            </>
          ) : null}

          {step === 'preview' ? (
            <>
              <button type="button" className="iw-btn" onClick={() => setStep('map')}>
                Back to mapping
              </button>
              <button
                type="button"
                className="iw-btn iw-btn--primary"
                disabled={!canCommit || busy}
                onClick={() => void handleCommit()}
              >
                Commit {preview ? `(${preview.validCount} row${preview.validCount === 1 ? '' : 's'})` : ''}
              </button>
            </>
          ) : null}

          {step === 'summary' ? (
            <button
              type="button"
              className="iw-btn iw-btn--primary"
              onClick={() => {
                onCommitted()
                onClose()
              }}
            >
              Done
            </button>
          ) : null}
        </div>
      </div>
    </FocusTrapDialog>
  )
}

function WizardSteps({ step }: { step: WizardStep }) {
  const steps: { key: WizardStep; label: string }[] = [
    { key: 'upload', label: '1. Upload' },
    { key: 'map', label: '2. Map columns' },
    { key: 'preview', label: '3. Preview' },
    { key: 'summary', label: '4. Summary' },
  ]
  return (
    <div role="list" aria-label="Import wizard steps" style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
      {steps.map((s) =>
        s.key === step ? (
          <StatusPill key={s.key} variant="info" label={s.label} />
        ) : (
          <StatusPill key={s.key} variant="neutral" label={s.label} icon="check" />
        ),
      )}
    </div>
  )
}

function UploadStep({
  noFiscalYear,
  fiscalYearIds,
  fiscalYearId,
  onFiscalYearChange,
  file,
  onFileChange,
}: {
  noFiscalYear: boolean
  fiscalYearIds: string[]
  fiscalYearId: string
  onFiscalYearChange: (id: string) => void
  file: File | null
  onFileChange: (file: File | null) => void
}) {
  if (noFiscalYear) {
    return (
      <InlineAlert severity="warning">
        This production has no fiscal year recorded yet — record one before importing spend lines.
      </InlineAlert>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span>Fiscal year</span>
        <select
          className="iw-input"
          value={fiscalYearId}
          onChange={(e) => onFiscalYearChange(e.target.value)}
          aria-label="Fiscal year"
        >
          {fiscalYearIds.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span>CSV file</span>
        <input
          type="file"
          accept=".csv,text/csv"
          aria-label="Spend-line CSV file"
          onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
        />
      </label>
      <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
        CSV only in this build (2 MB / 2,000 row ceiling) — XLSX support is a disclosed follow-up.
        {file ? ` Selected: ${file.name}.` : ''}
      </p>
    </div>
  )
}

function MapStep({
  batch,
  columnMapping,
  onChange,
}: {
  batch: SpendImportBatchView
  columnMapping: Record<string, string>
  onChange: (mapping: Record<string, string>) => void
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      <p style={{ margin: 0, color: 'var(--iw-text-muted)' }}>
        {batch.filename} — {batch.rowCount} row{batch.rowCount === 1 ? '' : 's'} detected. Map each field to a
        column from the file (leave blank if not present).
      </p>
      <table className="iw-table" aria-label="Column mapping">
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">CSV column</th>
          </tr>
        </thead>
        <tbody>
          {SPEND_IMPORT_CANONICAL_FIELDS.map((field) => (
            <tr key={field}>
              <td>{FIELD_LABEL[field] ?? field}</td>
              <td>
                <select
                  className="iw-input"
                  aria-label={`CSV column for ${FIELD_LABEL[field] ?? field}`}
                  value={columnMapping[field] ?? ''}
                  onChange={(e) => onChange({ ...columnMapping, [field]: e.target.value })}
                >
                  <option value="">— not mapped —</option>
                  {batch.headers.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function PreviewStep({ preview }: { preview: SpendImportPreviewView }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
        <StatusPill variant="success" label={`${preview.validCount} valid`} />
        <StatusPill variant="danger" label={`${preview.errorCount} error`} />
      </div>
      {preview.errorCount > 0 ? (
        <InlineAlert severity="warning">
          Rows with errors will be skipped at commit — never silently dropped, but not created either.
        </InlineAlert>
      ) : null}
      <table className="iw-table" aria-label="Row-by-row preview">
        <thead>
          <tr>
            <th scope="col">Row</th>
            <th scope="col">Status</th>
            <th scope="col">Details</th>
          </tr>
        </thead>
        <tbody>
          {preview.rows.map((row) => (
            <tr key={row.rowNumber}>
              <td>{row.rowNumber}</td>
              <td>
                <StatusPill variant={ROW_STATUS_VARIANT[row.status] ?? 'neutral'} label={row.status} />
              </td>
              <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                {row.violations.length === 0
                  ? '—'
                  : row.violations.map((v) => v.message ?? v.code).join('; ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SummaryStep({ result }: { result: SpendImportCommitResultView }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
        <StatusPill variant="success" label={`${result.createdCount} created`} />
        <StatusPill variant="warning" label={`${result.skippedCount} skipped`} />
        <StatusPill variant="info" label={`${result.adjustedCount} with a qualifying % override`} />
      </div>
      <table className="iw-table" aria-label="Commit outcome">
        <thead>
          <tr>
            <th scope="col">Row</th>
            <th scope="col">Outcome</th>
            <th scope="col">Details</th>
          </tr>
        </thead>
        <tbody>
          {result.rows.map((row) => (
            <tr key={row.rowNumber}>
              <td>{row.rowNumber}</td>
              <td>
                <StatusPill variant={ROW_STATUS_VARIANT[row.outcome] ?? 'neutral'} label={row.outcome} />
              </td>
              <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                {row.violations.length === 0
                  ? '—'
                  : row.violations.map((v) => v.message ?? v.code).join('; ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
